import { useState, type FormEvent } from 'react';
import { useSession } from '../contexts/SessionContext';
import { ApiError } from '../api/client';

export function LoginScreen() {
  const { login } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      // Engine returns "invalid email or password" — surface as-is.
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md"
        aria-label="Login"
      >
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">Sitekit Factory</h1>
        <p className="text-sm text-gray-500 mb-6">Sign in to manage your sites</p>

        <label className="block mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-1">Email</span>
          <input
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            disabled={submitting}
          />
        </label>

        <label className="block mb-5">
          <span className="block text-sm font-medium text-gray-700 mb-1">Password</span>
          <input
            type="password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={submitting}
          />
        </label>

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="w-full bg-brand text-white font-medium py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-xs text-gray-400 mt-6 text-center">
          Account created by your administrator. Contact them to reset your password.
        </p>
      </form>
    </div>
  );
}
